import {
  AGREEMENT_FIELD_GROUPS,
  AGREEMENT_FIELD_TYPES,
  AGREEMENT_LIMITS,
  AgreementField,
  AgreementFieldGroup,
  AgreementFieldType,
} from 'models/Agreement';
import { IconProp } from 'models/Icon';
import {
  AgreementBox,
  AgreementPageSize,
  AgreementPoint,
  getBoxCenteredAt,
} from 'agreementCoordinatesUtils';
import { getAgreementFieldGroup } from 'agreementUtils';
import { getArrayLength, getUniqueId } from 'utils';
import PencilIcon from 'icons/Pencilalt.icon';
import UserIcon from 'icons/User.icon';
import MailIcon from 'icons/Mail.icon';
import CalendarIcon from 'icons/Calendar.icon';
import ClockIcon from 'icons/Clock.icon';
import MobileIcon from 'icons/Mobile.icon';
import BuildingIcon from 'icons/Building.icon';
import IdCardIcon from 'icons/IdCard.icon';
import BriefcaseIcon from 'icons/Briefcase.icon';
import CashIcon from 'icons/Cash.icon';
import QuestionMarkCircleIcon from 'icons/QuestionMarkCircle.icon';
import ChecklistIcon from 'icons/Checklist.icon';
import { getClamped } from 'numberUtils';

export type FieldTypeConfig = {
  type: AgreementFieldType;
  labelKey: string;
  Icon: IconProp;
  defaultSize: { width: number; height: number };
  isRequiredByDefault?: boolean;
};

export const FIELD_TYPE_CONFIGS: FieldTypeConfig[] = [
  {
    type: AGREEMENT_FIELD_TYPES.SIGNATURE,
    labelKey: 'agreementFieldSignature',
    Icon: PencilIcon,
    defaultSize: { width: 180, height: 50 },
    isRequiredByDefault: true,
  },
  {
    type: AGREEMENT_FIELD_TYPES.FULL_NAME,
    labelKey: 'fullName',
    Icon: UserIcon,
    defaultSize: { width: 180, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.EMAIL,
    labelKey: 'email',
    Icon: MailIcon,
    defaultSize: { width: 180, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.DATE_SIGNED,
    labelKey: 'agreementFieldDateSigned',
    Icon: CalendarIcon,
    defaultSize: { width: 100, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.TIME_SIGNED,
    labelKey: 'agreementFieldTimeSigned',
    Icon: ClockIcon,
    defaultSize: { width: 100, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.PHONE,
    labelKey: 'mobile',
    Icon: MobileIcon,
    defaultSize: { width: 140, height: 20 },
    isRequiredByDefault: true,
  },
  {
    type: AGREEMENT_FIELD_TYPES.COMPANY,
    labelKey: 'agreementFieldCompany',
    Icon: BuildingIcon,
    defaultSize: { width: 180, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.ABN,
    labelKey: 'agreementFieldAbn',
    Icon: IdCardIcon,
    defaultSize: { width: 130, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.FUNDING_SOURCE,
    labelKey: 'agreementFieldFundingSource',
    Icon: BriefcaseIcon,
    defaultSize: { width: 160, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.PURCHASE_BUDGET,
    labelKey: 'agreementFieldPurchaseBudget',
    Icon: CashIcon,
    defaultSize: { width: 140, height: 20 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.CUSTOM_TEXT,
    labelKey: 'agreementFieldCustomQuestion',
    Icon: QuestionMarkCircleIcon,
    defaultSize: { width: 240, height: 100 },
  },
  {
    type: AGREEMENT_FIELD_TYPES.CHECKBOX,
    labelKey: 'agreementFieldCheckbox',
    Icon: ChecklistIcon,
    defaultSize: { width: 150, height: 20 },
  },
];

export const FIELD_PALETTE_GROUPS: {
  group: AgreementFieldGroup;
  titleKey?: string;
}[] = [
  { group: AGREEMENT_FIELD_GROUPS.SIGNATURE },
  { group: AGREEMENT_FIELD_GROUPS.AUTO, titleKey: 'agreementFieldsAutoFilled' },
  {
    group: AGREEMENT_FIELD_GROUPS.BUYER,
    titleKey: 'agreementFieldsBuyerFilled',
  },
];

export const createAgreementField = (
  { type, defaultSize, isRequiredByDefault }: FieldTypeConfig,
  page: number,
  center: AgreementPoint,
  pageSize: AgreementPageSize,
): AgreementField => ({
  id: getUniqueId(),
  type,
  required: !!isRequiredByDefault,
  page,
  ...getBoxCenteredAt(center, defaultSize, pageSize),
});

export const getIsFieldLimitReached = (fields: AgreementField[]) =>
  getArrayLength(fields) >= AGREEMENT_LIMITS.MAX_FIELDS;

export const getFieldTypeConfigsInGroup = (group: AgreementFieldGroup) =>
  FIELD_TYPE_CONFIGS.filter(
    (config) => getAgreementFieldGroup(config) === group,
  );

export const getFieldTypeConfig = (type: string) =>
  FIELD_TYPE_CONFIGS.find((config) => config.type === type);

export const FIELD_TYPE_DRAG_FORMAT = 'application/x-agreement-field-type';

export const getIsFieldTypeDrag = (dataTransfer: DataTransfer) =>
  Array.from(dataTransfer.types).includes(FIELD_TYPE_DRAG_FORMAT);

const FIELD_SETTINGS_GAP = 8;
const FIELD_SETTINGS_WIDTH = 250;
const FIELD_SETTINGS_MAX_HEIGHT = 220;

// Below the field, or above it when it would overflow the page bottom
export const getFieldSettingsPosition = (
  field: AgreementBox,
  page: AgreementBox,
) => {
  const below = field.y + field.height + FIELD_SETTINGS_GAP;
  const fitsBelow = below + FIELD_SETTINGS_MAX_HEIGHT <= page.height;

  return {
    left: getClamped(
      field.x,
      0,
      Math.max(0, page.width - FIELD_SETTINGS_WIDTH),
    ),
    top: fitsBelow ? below : undefined,
    bottom: fitsBelow ? undefined : page.height - field.y + FIELD_SETTINGS_GAP,
  };
};
